import React, { useEffect, useState } from "react";
import AgGridWrapper from "../UI/AgGrid";
import { toast } from "react-toastify";
import PrimaryModal from "../UI/Modal/PrimaryModal";
import { Formik, Form } from "formik";
import { FormikInput, PrimaryButton, SecondaryButton } from "../UI";
import * as Yup from "yup";

const ExperienceNameSchema = Yup.object().shape({
    exp_name: Yup.string().required("Experience name is required"),
});

const PublishExperienceModal = ({
    isOpen,
    onClose,
    experience,
    onCancelDraft,
    onCreateNewExperience,
    onPublish,
    loading
}) => {
    const [activations, setActivations] = useState([]);
    const [newExpName, setNewExpName] = useState("");
    const [showNewExpInput, setShowNewExpInput] = useState(false);
    const [showNewExpModal, setShowNewExpModal] = useState(false);

    const columnDefs = [
        { headerName: "Type", field: "type", flex: 1 },
        { headerName: "Id", field: "id", flex: 1 },
    ];

    // build activations from experience object
    useEffect(() => {
        if (!experience) return;

        const formatted = [];

        if (experience.associated_adobe_target_offer_id?.length) {
            experience.associated_adobe_target_offer_id.forEach((id) => {
                formatted.push({
                    id: id,
                    type: "Adobe Target",
                });
            });
        }

        if (experience.associated_contentful_offer_id?.length) {
            experience.associated_contentful_offer_id.forEach((id) => {
                formatted.push({
                    id: id,
                    type: "Contentful",
                });
            });
        }

        if (experience.associated_tealium_offer_id?.length) {
            experience.associated_tealium_offer_id.forEach((id) => {
                formatted.push({
                    id: id,
                    type: "Tealium",
                });
            });
        }

        if (experience.associated_github_entry_path?.length) {
            experience.associated_github_entry_path.forEach((id) => {
                formatted.push({
                    id: id,
                    type: "Github",
                });
            });
        }

        setActivations(formatted);
    }, [experience]);

    return (
        <>
            <PrimaryModal
                isOpen={isOpen}
                hideModal={onClose}
                title="Confirm Approval"
            >
                <div className="p-6 space-y-6">
                    <p className="text-sm font-medium text-gray-700">
                        Approval action will update all associated activations:
                    </p>

                    {activations?.length > 0 ? (
                        <div style={{ height: "280px", overflow: "auto", width: "100%" }}>
                            <AgGridWrapper
                                rowData={activations}
                                columnDefs={columnDefs}
                                domLayout="autoHeight"
                            />
                        </div>
                    ) : (
                        <p className="text-gray-500 text-sm">
                            No associated activations found.
                        </p>
                    )}

                    {showNewExpInput && (
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                placeholder="Enter new experience name"
                                className="border rounded px-3 py-2 text-sm w-full"
                                value={newExpName}
                                onChange={(e) => setNewExpName(e.target.value)}
                            />
                            <PrimaryButton
                                size="sm"
                                loading={loading}
                                handleClick={() => {
                                    if (!newExpName.trim()) {
                                        toast.error("Experience name is required");
                                        return;
                                    }
                                    onCreateNewExperience(newExpName);
                                }}
                            >
                                Create
                            </PrimaryButton>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4">
                        <SecondaryButton
                            handleClick={() => {
                                onCancelDraft();
                                onClose();
                            }}
                        >
                            Cancel
                        </SecondaryButton>

                        <SecondaryButton
                            handleClick={() => setShowNewExpModal(true)}
                        >
                            Create New Experience
                        </SecondaryButton>

                        <PrimaryButton
                            loading={loading}
                            handleClick={() => {
                                onPublish();
                            }}
                        >
                            Approve
                        </PrimaryButton>
                    </div>
                </div>
            </PrimaryModal>

            {showNewExpModal && (
                <PrimaryModal
                    isOpen={showNewExpModal}
                    hideModal={() => setShowNewExpModal(false)}
                    title="New Experience Name"
                    width="500px"
                >
                    <div className="p-6 space-y-6">
                        <Formik
                            initialValues={{ exp_name: "" }}
                            validationSchema={ExperienceNameSchema}
                            onSubmit={(values) => {
                                onCreateNewExperience(values.exp_name);
                                setShowNewExpModal(false);
                            }}
                        >
                            {() => (
                                <Form className="space-y-4 md:space-y-6">
                                    <FormikInput
                                        type="text"
                                        id="exp_name"
                                        name="exp_name"
                                        label="Experience Name"
                                    />
                                    <div className="flex items-center justify-end space-x-2">
                                        <PrimaryButton type="submit" loading={loading}>Save</PrimaryButton>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </PrimaryModal>
            )}
        </>
    );
};

export default PublishExperienceModal;
